import { useCallback } from "react";
import { api } from "@/helper/api";
import type {
  QuickSearchSelectionEnrichmentConfig,
  QuickSearchSelectionMetadata,
} from "@/types/QuickSearch";
import { normalizeQuickSearchResponse } from "@/helper/quickSearchSelection";

export type SubjectOption = {
  value: string;
  label: string;
  is_deleted?: boolean;
  companyId?: string;
  is_active?: boolean;
  is_parent?: boolean;
  selection?: QuickSearchSelectionMetadata;
};

interface UseSubjectSearchProps {
  subdomain: "admin" | "manage" | "portal";
  allowSubjectSearchForNonAdmin?: boolean;
  includeDeletedSubjects?: boolean;
  companyIdForFiltering?: string;
  subjectIdForFiltering?: string | string[];
  companyId?: string;
  parentCompanyIdToExclude?: string; //to filter out own company
  queryParameter?: "users" | "companies";
  filterParameter?: any;
  returnRawData?: boolean; // skip the prepared user/company response and respond with res.data
  entity?: string;
  mapResponseToOptions?: (res: any) => SubjectOption[];
  minSearchLength?: number;
  selectionEnrichment?: QuickSearchSelectionEnrichmentConfig | null;
}
type FetchSubjectsOverrides = Pick<
  UseSubjectSearchProps,
  | "queryParameter"
  | "parentCompanyIdToExclude"
  | "companyIdForFiltering"
  | "subjectIdForFiltering"
  | "includeDeletedSubjects"
  | "returnRawData"
  | "entity"
  | "mapResponseToOptions"
  | "filterParameter"
  | "minSearchLength"
  | "selectionEnrichment"
>;

const cleanOptions = (options: SubjectOption[]): SubjectOption[] => {
  if (!Array.isArray(options)) return [];
  const seen = new Set<string>();
  return options.filter((opt) => {
    if (!opt?.value) return false;
    if (seen.has(String(opt.value))) return false;
    seen.add(String(opt.value));
    return true;
  });
};

export const useSubjectSearch = ({
  subdomain,
  allowSubjectSearchForNonAdmin = false,
  includeDeletedSubjects = false,
  companyIdForFiltering,
  subjectIdForFiltering,
  companyId,
  parentCompanyIdToExclude,
  queryParameter,
  filterParameter,
  returnRawData = false,
  entity,
  mapResponseToOptions,
  minSearchLength = 3,
  selectionEnrichment,
}: UseSubjectSearchProps) => {
  const fetchSubjects = useCallback(
    (
      inputValue: string,
      overrides?: FetchSubjectsOverrides,
    ): Promise<SubjectOption[]> => {
      const effectiveMinSearchLength =
        overrides?.minSearchLength ?? minSearchLength;
      if (
        effectiveMinSearchLength > 0 &&
        inputValue.length < effectiveMinSearchLength
      ) {
        return Promise.resolve([]);
      }

      const tempEntity = overrides?.entity || entity || "subject/quicksearch";

      const effectiveQueryParam = overrides?.queryParameter ?? queryParameter;
      const effectiveParentExclude =
        overrides?.parentCompanyIdToExclude ?? parentCompanyIdToExclude;

      const effectiveFilterParameter =
        overrides?.filterParameter ?? filterParameter;
      const effectiveSelectionEnrichment =
        overrides?.selectionEnrichment !== undefined
          ? overrides.selectionEnrichment
          : selectionEnrichment;

      const isSearchAllowed =
        subdomain === "admin" || allowSubjectSearchForNonAdmin;
      if (!isSearchAllowed) return Promise.resolve([]);

      const additionalUrlParameters: Record<string, any> = {
        search: inputValue,
      };

      if (effectiveQueryParam)
        additionalUrlParameters["show"] = effectiveQueryParam;

      if (effectiveSelectionEnrichment)
        additionalUrlParameters.enrich_response_with =
          effectiveSelectionEnrichment.responseField;

      if (includeDeletedSubjects || overrides?.includeDeletedSubjects)
        additionalUrlParameters["include_deleted"] = "true";

      if (subdomain === "admin" || allowSubjectSearchForNonAdmin) {
        if (overrides?.companyIdForFiltering || companyIdForFiltering)
          additionalUrlParameters.company_id =
            overrides?.companyIdForFiltering ?? companyIdForFiltering;

        if (overrides?.subjectIdForFiltering || subjectIdForFiltering) {
          if (
            Array.isArray(subjectIdForFiltering) ||
            Array.isArray(overrides?.subjectIdForFiltering)
          ) {
            additionalUrlParameters.company_subject__in =
              overrides?.subjectIdForFiltering ?? subjectIdForFiltering;
          } else {
            additionalUrlParameters.company_subject =
              overrides?.subjectIdForFiltering ?? subjectIdForFiltering;
          }
        }
      }

      if (effectiveFilterParameter) {
        Object.assign(additionalUrlParameters, effectiveFilterParameter);
      }

      return new Promise<SubjectOption[]>((resolve, reject) => {
        api.genericApiRequest({
          method: "get",
          entity: tempEntity,
          getAllPages: true,
          parametersToRender: { additionalUrlParameters, depth: "0" },
          successHandler: (res: any) => {
            try {
              const responseData = effectiveSelectionEnrichment
                ? normalizeQuickSearchResponse(
                    res.data,
                    effectiveSelectionEnrichment,
                  )
                : res.data;
              if (overrides?.mapResponseToOptions) {
                resolve(overrides?.mapResponseToOptions(responseData));
                return;
              }

              const shouldReturnRaw = overrides?.returnRawData ?? returnRawData;

              if (shouldReturnRaw) {
                resolve(responseData);
                return;
              }

              const users = (responseData as any)["users"] || [];
              const companies = (responseData as any)["companies"] || [];

              const userOptions: SubjectOption[] = users.map((u: any) => ({
                value: u.subject_id,
                label: `${u.name} (${u.email} - ${u.company_name})`,
                is_deleted: u.is_deleted,
                ...(u.selection ? { selection: u.selection } : {}),
              }));

              const companyOptions: SubjectOption[] = companies
                .map((c: any) => ({
                  value: c.subject_id,
                  label: `${c.name} (${c.customer_id})${
                    c.is_parent ? " (Parent)" : ""
                  }`,
                  companyId: c.id,
                  is_active: c.is_active,
                  is_parent: c.is_parent,
                  ...(c.selection ? { selection: c.selection } : {}),
                }))
                .filter((c: any) => c.companyId !== effectiveParentExclude);

              //Sort companies for parents first
              companyOptions.sort((a: any, b: any) => {
                if (a.is_parent && !b.is_parent) return -1;
                if (!a.is_parent && b.is_parent) return 1;
                return 0;
              });
              const combined = [...companyOptions, ...userOptions];

              resolve(combined);
            } catch (err) {
              reject(err);
            }
          },
          failHandler: (err: any) => {
            reject(err);
          },
        });
      });
    },
    [
      subdomain,
      allowSubjectSearchForNonAdmin,
      includeDeletedSubjects,
      companyIdForFiltering,
      subjectIdForFiltering,
      companyId,
      parentCompanyIdToExclude,
      queryParameter,
      returnRawData,
      entity,
      mapResponseToOptions,
      minSearchLength,
      selectionEnrichment,
    ],
  );
  const fetchSubjectsByIds = useCallback(
    (
      ids: string[],
      overrides?: FetchSubjectsOverrides,
    ): Promise<SubjectOption[]> => {
      if (!ids?.length) return Promise.resolve([]);

      const tempEntity = overrides?.entity || entity || "subject";
      const additionalUrlParameters = { id__in: ids.join(",") };

      return new Promise<SubjectOption[]>((resolve, reject) => {
        api.genericApiRequest({
          method: "get",
          entity: tempEntity,
          getAllPages: true,
          parametersToRender: { additionalUrlParameters },
          successHandler: (res: any) => {
            try {
              const mapper =
                overrides?.mapResponseToOptions || mapResponseToOptions;
              if (mapper) {
                resolve(cleanOptions(mapper(res.data)));
                return;
              }

              const data = res.data.results || res.data;
              const options = data.map((item: any) => ({
                value: item.id ?? item.subject_id,
                label: item.name ?? item.label ?? String(item.id),
              }));
              resolve(options);
            } catch (err) {
              reject(err);
            }
          },
          failHandler: reject,
        });
      });
    },
    [entity, mapResponseToOptions],
  );

  return { fetchSubjects, fetchSubjectsByIds };
};
